import birdhouse from "./birdhouse.json";
import bookshelf from "./bookshelf.json";
import crate from "./crate.json";
import crosscutSled from "./crosscutSled.json";
import hexFrame from "./hexFrame.json";
import jewelryBox from "./jewelryBox.json";
import materialShelf from "./materialShelf.json";
import pictureFrame from "./pictureFrame.json";
import planterBox from "./planterBox.json";
import rusticFrame from "./rusticFrame.json";
import rusticShelf from "./rusticShelf.json";
import servingTray from "./servingTray.json";
import shelf from "./shelf.json";
import sideTable from "./sideTable.json";
import stepStool from "./stepStool.json";
import storageRack from "./storageRack.json";
import straightLineSled from "./straightLineSled.json";
import toolDrawers from "./toolDrawers.json";
import worktable1x1 from "./worktable1x1.json";
import worktable1x2 from "./worktable1x2.json";

/**
 * The plan drawer's contents: every assembly build in the game, one
 * JSON record apiece (see `bench-work/plan-record.ts` for the schema
 * and `parsePlanRecord` for what a record may say). A new build is a
 * new file here plus its import below — the blueprint editor
 * (`/blueprints.html` on the dev server) writes the file.
 *
 * Two lists because their ids mean different things downstream: a
 * product plan's id is a `FinishedProductType` (the material the build
 * leaves on the bench), and an equipment plan's id names the machine,
 * upgrade, or jig the build grants. Within a list, file order is the
 * drawer's order inside each category.
 *
 * The records are raw here — validated once, in `plan-record.ts` — so
 * that `Materials.ts` can read the product ids off this module without
 * importing anything that reads `Materials.ts`.
 */

export const PRODUCT_PLAN_FILES = {
  rusticShelf,
  rusticFrame,
  birdhouse,
  crate,
  planterBox,
  stepStool,
  pictureFrame,
  hexFrame,
  servingTray,
  jewelryBox,
  shelf,
  sideTable,
  bookshelf,
} as const;

export const EQUIPMENT_PLAN_FILES = {
  worktable1x1,
  worktable1x2,
  storageRack,
  toolDrawers,
  materialShelf,
  crosscutSled,
  straightLineSled,
} as const;

export type ProductPlanId = keyof typeof PRODUCT_PLAN_FILES;
export type EquipmentPlanId = keyof typeof EQUIPMENT_PLAN_FILES;
export type PlanId = ProductPlanId | EquipmentPlanId;

export const PRODUCT_PLAN_IDS = Object.keys(
  PRODUCT_PLAN_FILES,
) as ReadonlyArray<ProductPlanId>;
export const EQUIPMENT_PLAN_IDS = Object.keys(
  EQUIPMENT_PLAN_FILES,
) as ReadonlyArray<EquipmentPlanId>;
export const PLAN_IDS: ReadonlyArray<PlanId> = [
  ...PRODUCT_PLAN_IDS,
  ...EQUIPMENT_PLAN_IDS,
];

export const RAW_PLAN_RECORDS: Readonly<Record<PlanId, unknown>> = {
  ...PRODUCT_PLAN_FILES,
  ...EQUIPMENT_PLAN_FILES,
};

export function isProductPlanId(id: string): id is ProductPlanId {
  return Object.prototype.hasOwnProperty.call(PRODUCT_PLAN_FILES, id);
}
